/**
 * Which long edges of a countertop are exposed.
 *
 * A wall-backed run's slab has ONE exposed long edge (the front); its back
 * meets the wall. A free-standing slab — island or peninsula — is exposed on
 * BOTH long sides, so the user's edge profile must be cut into the back edge
 * too, and its ends default to matching the front rather than staying square.
 *
 * Three producers, three tags, one predicate. The generator's `buildIsland*`
 * writes `category.island`; its peninsula strip writes `category.peninsula`;
 * the cabinet-run engine's free-standing segments (drawn, auto-derived, and
 * scanned islands alike) emit their slab through `buildCountertopRuns` — the
 * same path a wall run uses — so it would otherwise carry only
 * `{ countertop: true }`, and `fillFreestandingSegment` stamps
 * `category.freestanding` on it (the tag its back panel already carries).
 *
 * A slab persisted BEFORE that tag existed carries none of the three, and a
 * countertop edit is a per-object field write that never re-materializes it.
 * So the predicate also derives the answer from ownership: the slab lists the
 * cabinets beneath it as `parents`; a cabinet carries its `runId` and the id
 * of the wall it stands on; and a run's cabinet stands either on one of the
 * run's WALL segments or on a wall the engine synthesized for a free-standing
 * segment. A cabinet in a run that has free-standing segments, standing on no
 * wall segment of that run, is on an island.
 *
 * Every consumer must go through this predicate rather than testing one tag.
 */

const { findRunByIdOrAlias } = require('./runIds');

/**
 * Does this run cabinet stand on one of its run's synthesized virtual walls?
 * False when the cabinet's run is unknown or has no free-standing segment.
 */
function standsOnVirtualWall(cabinet, cabinetRuns) {
  const run = findRunByIdOrAlias(cabinetRuns, cabinet.runId);
  const segments = run?.segments || [];
  if (!segments.some((s) => s?.freestanding)) return false;
  const wallIds = cabinet.parentWallIds || [];
  return !wallIds.some((id) =>
    segments.some((s) => !s?.freestanding && s?.wallId === id)
  );
}

/**
 * @param {Object} countertop
 * @param {{ objects?: Object[], cabinetRuns?: Object[] }} [ctx]
 *   The room's objects (to resolve `parents`) and its stored cabinet runs.
 *   Optional — without them only the producer tags are consulted.
 * @returns {boolean} true when both long edges are exposed
 */
function isFreestandingCountertop(countertop, { objects, cabinetRuns } = {}) {
  const cat = countertop?.category;
  if (cat && (cat.island || cat.peninsula || cat.freestanding)) return true;
  if (!objects?.length || !cabinetRuns?.length) return false;
  for (const p of countertop?.parents || []) {
    const parent = objects.find((o) => o?.id === p?.id);
    // Only a lower cabinet says where a slab stands: appliances carry no wall,
    // corner units carry two and never sit on a virtual wall.
    if (parent?.type !== 'lower-cabinet' || !parent.runId) continue;
    if (standsOnVirtualWall(parent, cabinetRuns)) return true;
  }
  return false;
}

/**
 * How a countertop's END edges finish when the user has not chosen: a
 * free-standing top matches its front profile, a wall-backed run (ends against
 * cabinets or walls) stays square. Same signature as the predicate.
 */
function defaultEndFinish(countertop, ctx) {
  return isFreestandingCountertop(countertop, ctx) ? 'match' : 'square';
}

/**
 * Tag a cabinet-run-engine countertop as free-standing. Pure — returns a new
 * object; the category object is copied, not mutated.
 */
function markCountertopFreestanding(countertop) {
  return {
    ...countertop,
    category: { ...(countertop.category || {}), freestanding: true },
  };
}

module.exports = {
  isFreestandingCountertop,
  defaultEndFinish,
  markCountertopFreestanding,
};
